import React, { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';


/**
 * Base Widget Component
 * Provides common functionality for all widgets:
 * - Loading states
 * - Error handling
 * - Controlled chart/table display via `mode` prop (toggle lives in CardHeader)
 * - Horizontal flip animation when switching between chart and table
 * - Consistent minimum height across ALL states so the card shell never resizes
 */
function WidgetBase({
    loading = false,
    error = null,
    chartComponent,
    tableComponent,
    mode = 'chart',
    emptyMessage = 'No data available',
    chartAlign = 'center'   // 'center' | 'bottom'
}) {
    // What is currently rendered (lags behind `mode` during the flip)
    const [displayMode, setDisplayMode] = useState(mode);
    // 'idle' | 'out' | 'in'
    const [flipState, setFlipState] = useState('idle');
    // Locked height captured just before the flip starts
    const [lockedHeight, setLockedHeight] = useState(null);
    const containerRef = useRef(null);

    // When the external mode changes, snapshot the current height and kick off the "out" phase
    useEffect(() => {
        if (mode === displayMode) return;
        if (containerRef.current) {
            setLockedHeight(containerRef.current.offsetHeight);
        }
        setFlipState('out');
    }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Drive the two-phase animation
    useEffect(() => {
        if (flipState === 'out') {
            // After rotating to 90deg (edge-on), swap content and start "in"
            const t = setTimeout(() => {
                setDisplayMode(mode);
                setFlipState('in');
            }, 220);
            return () => clearTimeout(t);
        }
        if (flipState === 'in') {
            // After rotating back to 0deg, release the locked height and go idle
            const t = setTimeout(() => {
                setFlipState('idle');
                setLockedHeight(null);
            }, 220);
            return () => clearTimeout(t);
        }
    }, [flipState, mode]);

    // Loading state – centered spinner that fills the card height
    if (loading) {
        return (
            <div className="w-full flex items-center justify-center min-h-[330px]">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    // Error state — treat as no data; do not surface API errors to the user
    if (error) {
        return (
            <div className="w-full flex items-center justify-center min-h-[330px]">
                <p className="text-primary/70">{emptyMessage}</p>
            </div>
        );
    }

    // No data (both sides are null)
    if (!chartComponent && !tableComponent) {
        return (
            <div className="w-full flex items-center justify-center min-h-[330px]">
                <p className="text-primary/70">{emptyMessage}</p>
            </div>
        );
    }

    // Single-mode: only one component provided (e.g. TableWidget passes chartComponent=null).
    // Render it directly — no flip needed, but still enforce consistent min-height and centering.
    if (!chartComponent || !tableComponent) {
        return (
            <div className="flex flex-col justify-center w-full min-h-[330px]">
                {chartComponent || tableComponent}
            </div>
        );
    }

    // Dual-mode: render with flip animation inside a fixed-min-height container
    return (
        <div
            ref={containerRef}
            className="w-full min-h-[330px]"
            style={{
                perspective: '1200px',
                // Lock height during the entire flip to prevent layout shift
                ...(lockedHeight != null ? { height: lockedHeight, overflow: 'hidden' } : {})
            }}
        >
            {/* 'in' phase: keyframe starts at -90deg so the new content sweeps in from the opposite edge */}
            <div
                key={flipState === 'in' ? 'flip-in' : 'flip-other'}
                style={
                    flipState === 'in'
                        ? { animation: 'widgetFlipIn 0.22s ease-out forwards' }
                        : flipState === 'out'
                        ? { transform: 'rotateY(90deg)', transition: 'transform 0.22s ease-in', transformOrigin: 'center center' }
                        : {}
                }
            >
                {displayMode === 'chart' ? (
                    // Chart mode: justify-center (default) or justify-end (bottom-aligned bar charts).
                    // NO items-center so charts stretch to full width.
                    // No-data text uses text-center for horizontal alignment.
                    <div className={`flex flex-col ${chartAlign === 'bottom' ? 'justify-end' : 'justify-center'} w-full min-h-[330px]`}>
                        {chartComponent || (
                            <p className="w-full text-center text-primary/70">{emptyMessage}</p>
                        )}
                    </div>
                ) : (
                    // Table mode: same pattern — justify-center vertically, w-full keeps DataTable full-width.
                    <div className="flex flex-col justify-center w-full min-h-[330px]">
                        {tableComponent || (
                            <p className="w-full text-center text-primary/70">No Data Available</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default WidgetBase;
